import { Add, AddComment, OverflowMenuHorizontal, Star, View as ViewIcon } from "@carbon/icons-react";
import { Card, FlowBox, HorizBox, HoverView, Pad, PadBox, Separator, VertBox, WindowTitle } from "component/basics";
import { Heading, TextField, UtilityText } from "component/text";
import { EditorialDashConfig } from "structure/editorial-dash";
import { Feature, labelToKey, nullS, t, TLabel } from "util/stdtypes";
import { Image, ScrollView, StyleSheet, View } from "react-native";
import { useGlobalQuestions } from "component/question";
import { colorBorderPrimary, colorBorderSelected, colorSurfacePrimary, colorSurfaceSecondary, colorTagEmphasized, colorTagSuccess, colorTextOnBlackBackground, colorTextPrimary, colorTextSecondary } from "component/color";
import { useConfig } from "util/features";
import React, { useState } from "react";
import { Catcher, CatchList } from "system/catcher";
import { ActionButton, CTAButton, FilterButton, IconButton, Tag, TextButton } from "component/button";
import { Datastore, useDatastore, useSessionData } from "util/datastore";
import { FormField, SearchField } from "component/form";
import { makeAssetUrl, toTitleCase } from "util/util";
import { useGlobalTopics } from "structure/topic";
import { formatDate } from "component/date";
import { Popup } from "component/popup";
import { ConversationScreen } from "component/screen";


export type Space = {
    key: string;
    name: string;
    structureKey: string;
    commentCount?: number;
    format?: string;
    status?: string;
    time?: number;
}

export type SpaceFilter = {
    key: string;
    widget: React.ComponentType<{}>;
    filter: (datastore: Datastore, space: Space) => boolean;
}

export type SpaceWidgetProps = {
    space: Space;
}
export type SpaceColumn = {
    label: TLabel;
    alignRight?: boolean;
    widget: React.ComponentType<SpaceWidgetProps>;
}

export const ResponsesColumn : SpaceColumn = {
    label: t('Responses'),
    widget: ({space}) => 
        <UtilityText type='small' 
            text={space.commentCount?.toString() ?? '0'}
        />,
}

function FormatWidget({space}: SpaceWidgetProps) {
    const {templates} = useConfig<SpacesConfig>();
    const template = templates.find((template) => template.key === space.format);
    if (!template) return null;
    return <Tag label={template?.label} compact color={colorSurfaceSecondary} />
}

export const FormatColumn : SpaceColumn = {
    label: t('Format'),
    widget: FormatWidget,
}

export const CreatedColumn : SpaceColumn = {
    label: t('Created'),
    widget: ({space}) => 
        <UtilityText type='small' 
            text={formatDate(space.time) ?? 'N/A'}
        />,
}

export function StatusWidget({space}: SpaceWidgetProps) {
    const status = space.status ?? 'active';
    switch (status) {
    case 'active':
        return <Tag emoji='🚀' label={t('Active')} compact color={colorTagSuccess}/>
    case 'closed':
        return <Tag emoji='🔒' label={t('Closed')} compact color={colorTagEmphasized}/>
    default:
        return <Tag label={t('N/A')} compact color={colorSurfaceSecondary}/>
    }
}

export const StatusColumn : SpaceColumn = {
    label: t('Status'),
    widget: StatusWidget,
}

export function ActionsWidget({space}: SpaceWidgetProps) {
    const s = ActionWidgetStyle;
    const datastore = useDatastore();
    const [hover, setHover] = useState(false);
    const {actions} = useConfig<SpacesConfig>();
    function popupContent({onClose}) {
        return <View>
            {actions.map((action) => (
                <ActionButton key={labelToKey(action.label)} icon={action.icon} label={action.label} 
                    onPress={() => action.action(datastore, space).then(() => onClose())}
                />
            ))}
        </View>
    }
    return <View style={s.alignRight}>
        <Popup popupContent={popupContent} onClose={() => {}} setHover={setHover}>
            <ActionButton icon={OverflowMenuHorizontal} onPress={() => {}} hover={hover} />
        </Popup>
    </View>
}
const ActionWidgetStyle = StyleSheet.create({
    alignRight: {
        alignSelf: 'flex-end'
    }
})

export const ActionsColumn : SpaceColumn = {
    label: t('Actions'),
    alignRight: true,
    widget: ActionsWidget,
}

export function ConversationFormatPreview({template, selected, onPress}: 
    {template: SpaceTemplate, selected: boolean, onPress: () => void}
) {
    const s = ConversationFormatPreviewStyle;
    return <HoverView onPress={onPress} style={s.container}>
        <View style={selected ? s.selected : s.default}>
            <Image source={{uri: makeAssetUrl(template.imageUrl)}} style={s.image}/>
        </View>
        <Pad size={8}/>
        <UtilityText type='small' weight='medium' label={template.label}/>
    </HoverView>
}
const ConversationFormatPreviewStyle = StyleSheet.create({
    container: {
    },
    image: {
        width: 187,
        height: 130
    },
    selected: {
        borderColor: colorBorderSelected,
        borderWidth: 1,
        borderRadius: 4,
    },
    default: {
        borderColor: colorSurfaceSecondary,
        borderWidth: 1,
        borderRadius: 4,
    }
})

export function CoversationFormatEditor({spaceProps, setSpaceProps, selectedTemplateKey, setSelectedTemplateKey}) {
    const {templates} = useConfig<SpacesConfig>();
    const selectedTemplate = templates.find((template) => template.key === selectedTemplateKey);
    return <View>
        <UtilityText type='small' weight='medium' label={t('Prosocial format (required)')}/>
        <Pad size={8}/>
        <FlowBox columnGap={16} rowGap={16}>
            {templates.map((template) => (
                <ConversationFormatPreview key={template.key} template={template} 
                    selected={selectedTemplateKey === template.key} 
                    onPress={() => {
                        setSelectedTemplateKey(template.key);
                        setSpaceProps({});
                    }}
                />
            ))}
        </FlowBox>
        {selectedTemplate?.configWidget && <PadBox vert={32}>
            <selectedTemplate.configWidget spaceProps={spaceProps} setSpaceProps={setSpaceProps}/>
        </PadBox>}
    </View>
}

async function createSpaceAsync(datastore: Datastore, template: SpaceTemplate, name: string, spaceProps: SpaceProps) {
    let features = {};
    if (template?.featuresEnabled) {
        features = template.featuresEnabled.reduce((acc, feature) => {
            acc[feature] = true;
            return acc;
        }, {});
    }
    if (template?.featuresDisabled) {
        features = template.featuresDisabled.reduce((acc, feature) => {
            acc[feature] = false;
            return acc;
        }, features);
    }
    if (template?.createSpace) {
        await template.createSpace(datastore, name, spaceProps);
    } else {
        await datastore.callServerAsync('questions', 'add', {
            newInstance: {name, features},
            globals: spaceProps,
            format: template.key
        });
    }
    datastore.goBack();
}

export function NewSpaceScreen() {
    const {templates} = useConfig<SpacesConfig>();
    const [selectedTemplateKey, setSelectedTemplateKey] = useState(nullS);
    const [spaceProps, setSpaceProps] = useState({});
    const [name, setName] = useState(nullS);
    const datastore = useDatastore();
    const selectedTemplate = templates.find((template) => template.key === selectedTemplateKey);
    const createDisabled = !selectedTemplate || !name;

    if (templates.length === 0) {
        return <ConversationScreen noWrap>
            <View>
                <Pad size={32}/>
                <Heading type='large' weight='medium' label={t('No templates available')}/>
            </View>
        </ConversationScreen>
    }

    return <ConversationScreen noWrap>
        <View>
            <Pad size={32}/>
            <Heading type='large' weight='medium' label={t('New Space')}/>
            <Pad size={32}/>
            <FormField label={t('Prompt (required)')} >
                <TextField multiline={false} placeholder={t('Write a question or discussion prompt...')}
                    value={name} onChangeText={setName}
                />
            </FormField>
            <Pad size={32}/>
            <CoversationFormatEditor spaceProps={spaceProps} setSpaceProps={setSpaceProps} 
                selectedTemplateKey={selectedTemplateKey} setSelectedTemplateKey={setSelectedTemplateKey}
            />
        </View>
        <Pad size={32}/>
        <HorizBox right>
            <CTAButton type='secondary' label={t('Cancel')} onPress={() => datastore.goBack()}/>
            <Pad size={16}/>
            <CTAButton label={t('Create Space')} 
                onPress={() => createSpaceAsync(datastore, selectedTemplate!, name!, spaceProps)} 
                disabled={createDisabled}/>
        </HorizBox>
    </ConversationScreen>
}

export function SpacesTableRows({spaceList}: {spaceList: Space[]}) {
    const s = SpacesTableRowsStyle;
    const datastore = useDatastore();
    const {columns} = useConfig<SpacesConfig>();
    return <View>
        <HorizBox spread>
            <View style={s.titleCol}>
                <UtilityText caps type='small' label={t('Name')} color={colorTextSecondary}/>
            </View>
            {columns.map((column) => (
                <View key={labelToKey(column.label)} style={[s.col]}>
                    <View style={column.alignRight ? s.alignRight : null}>
                        <UtilityText caps type='small' label={column.label} key={labelToKey(column.label)} color={colorTextSecondary}/>
                    </View>
                </View>
            ))}
        </HorizBox>
        <CatchList items={spaceList} renderItem={(space, isLast) => (
            <PadBox vert={20} key={space.key}>
                <HorizBox center>
                    <View style={s.titleCol}>
                        <TextButton type='small' strong text={space.name} onPress={() => {
                            datastore.gotoInstance({
                                structureKey: space.structureKey,
                                instanceKey: space.key,
                            });
                        }}/>
                    </View>
                    {columns.map((column) => (
                        <View style={s.col} key={labelToKey(column.label)}>
                            <column.widget space={space}/>
                        </View>
                    ))}
                </HorizBox>
            </PadBox>
        )} separator={Separator}/>
    </View>
}
const SpacesTableRowsStyle = StyleSheet.create({
    alignRight: {
        alignSelf: 'flex-end'
    },
    titleCol: {
        flex: 3,
        flexBasis: 0,
        paddingRight: 16,
    },
    col: {
        flex: 1,
        flexBasis: 0,
        paddingRight: 16,
    }
})


function useSpaceList() : Space[] {
    const questionList = useGlobalQuestions();
    const topicList = useGlobalTopics();

    const questionSpaceList = questionList.map((question) => ({
        ...question, key: question.key, name: question.question,
        structureKey: 'question',
    }));

    const topicSpaceList = topicList.map((topic) => ({
        ...topic, key: topic.key, name: topic.name,
        structureKey: 'topic',
    }));

    const combinedSpaceList = [...questionSpaceList, ...topicSpaceList];

    const sortedSpaceList = combinedSpaceList.sort((a, b) => (b.time ?? 0) - (a.time ?? 0));

    return sortedSpaceList;
}

export function SpacesTable() {
    const s = SpacesTableStyle;
    const {filters} = useConfig<SpacesConfig>();
    const datastore = useDatastore();
    const [search, setSearch] = useState('');
    const spaceList = useSpaceList();

    const searchFilteredSpaceList = spaceList.filter((space) => 
        space.name.toLowerCase().includes(search.toLowerCase())
    );
    const filteredSpaceList = searchFilteredSpaceList.filter((space) => 
        filters.every((filter) => filter.filter(datastore, space))
    );

    return <View style={s.table}>
        <HorizBox spread center>
            <Heading type='large' weight='medium' label={t('Spaces')}/>
            <SearchField size='small' placeholder='Search...' value={search} onChangeText={setSearch}/>
        </HorizBox>
        <Pad size={20}/>
        <HorizBox spread center>
            <HorizBox>
                {filters.map((filter) => (
                    <filter.widget key={filter.key}/>
                ))}
            </HorizBox>
            <CTAButton 
                icon={<Add style={{width: 20, height: 20, fill: colorTextOnBlackBackground}}/>} 
                size='compact' label={t('New Space')} 
                onPress={() => datastore.pushSubscreen('newSpace')}
            />
        </HorizBox>
        <Pad size={24}/>
        <SpacesTableRows spaceList={filteredSpaceList}/>
    </View>
}

const SpacesTableStyle = StyleSheet.create({
    table: {
        backgroundColor: colorSurfacePrimary,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 20
    }
})

export function SpaceFilterWidgetList() {
    const selected = useSessionData('spaceFilter');
    const datastore = useDatastore();
    return <HorizBox gap={16} center>
        <FilterButton emoji='🚀'label={t('Active')} selected={selected === 'active'} onPress={() => {
            datastore.setSessionData('spaceFilter', selected === 'active' ? null : 'active');
        }}/>
        <FilterButton emoji='🔒' label={t('Closed')} selected={selected === 'closed'} onPress={() => {
            datastore.setSessionData('spaceFilter', selected === 'closed' ? null : 'closed');
        }}/>
    </HorizBox>
}

export function spaceFilter(datastore: Datastore, space: Space) {
    const selected = datastore.getSessionData('spaceFilter');
    if (selected === 'active') {
        return space.status === 'active' || !space.status;
    } else if (selected === 'closed') {
        return space.status === 'closed';
    } else {
        return true;
    }
}
export const StateFilter : SpaceFilter = {
    key: 'state',
    widget: SpaceFilterWidgetList,
    filter: spaceFilter,
}

export const ActionPreview : SpaceAction = {
    label: t('Preview'),
    icon: ViewIcon,
    action: async (datastore: Datastore, space: Space) => {
        return datastore.gotoInstance({
            structureKey: space.structureKey,
            instanceKey: space.key,
        });
    }
}


export type SpaceAction = {
    label: TLabel;
    icon: React.ComponentType<{}>;
    action: (datastore: Datastore, space: Space) => Promise<void>;
}

export type SpaceProps = Record<string, any>;

export type SpaceTemplate = {
    key: string;
    label: TLabel;
    imageUrl: string;
    structureKey: string,
    promptLabel?: TLabel,
    promptPlaceholder?: TLabel,
    featuresEnabled?: string[],
    featuresDisabled?: string[],
    nameChecker?: (name: string) => Promise<boolean | string>,
    createSpace?: (datastore: Datastore, name: string, spaceProps: SpaceProps) => Promise<string>,
    configWidget?: React.ComponentType<{spaceProps: SpaceProps, setSpaceProps: (spaceProps: SpaceProps) => void}>,
}

export type SpacesConfig = {
    filters: SpaceFilter[];
    columns: SpaceColumn[];
    actions: SpaceAction[];
    templates: SpaceTemplate[];
}

export const SpacesFeature : Feature<EditorialDashConfig, SpacesConfig> = {
    key: 'spaces',
    name: 'Spaces',
    subscreens: {
        newSpace: NewSpaceScreen
    },
    providesConfig: {
        dashboardTabs: [
            {
                key: 'spaces',
                label: t('Spaces'),
                icon: AddComment,
                component: SpacesTable,
            }
        ]
    },
    receivesConfigDefaults: {
        filters: [StateFilter],
        columns: [FormatColumn, CreatedColumn, ResponsesColumn, 
            StatusColumn, ActionsColumn
        ],
        actions: [ActionPreview],
        templates: []
    }
}

